import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  IconButton,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { PhotoUpload } from '@/components/PhotoUpload';
import { PhotoUploadError } from '@/components/PhotoUpload/types';

interface CategoryFormData {
  name: string;
  description?: string;
  icon?: File;
  displayOrder: number;
  isActive: boolean;
}

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CategoryFormData) => void;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }
    onSave(formData);
    setFormData({
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true
    });
    setError(null);
  };

  const handlePhotoError = (error: PhotoUploadError | null) => {
    setError(error?.message || null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 2
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Create New Category</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              error={!!error}
              helperText={error}
              fullWidth
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Category Icon
              </Typography>
              <PhotoUpload
                value={formData.icon}
                onChange={(file) => setFormData({ ...formData, icon: file ?? undefined })}
                onError={handlePhotoError}
                maxSize={2 * 1024 * 1024} // 2MB
              />
            </Box>

            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active Category"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formData.name.trim()}
          >
            Create Category
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};